import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { generateStarterCards } from '@/utils/starterCards';
import { FOLDER_COLORS } from '@/lib/folderColors';

const PROFILE_CACHE_KEY = 'mindfull_profile_cache';

function cacheProfileToLocalStorage(profile: UserProfile) {
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
  } catch {
    // localStorage might be full or unavailable
  }
}

function loadProfileFromLocalStorage(): UserProfile | null {
  try {
    const cached = localStorage.getItem(PROFILE_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

export interface Goal {
  id: string;
  text: string;
  phase: 'exploring' | 'planned' | 'executing';
  priority: number;
  board_id: string | null;
  status: 'active' | 'paused' | 'completed' | 'dropped';
  created_at: string;
  completed_at: string | null;
  last_activity_at: string;
  phase_history: Array<{ phase: string; entered_at: string }>;
}

export interface UserProfile {
  id: string;
  user_id: string;
  display_name: string | null;
  role: string | null;
  roles: string[];
  role_other: string | null;
  goals: Goal[];
  patterns: Record<string, any>;
  partnr_preferences: Record<string, any>;
  mind_dumps: Array<Record<string, any>>;
  onboarding_completed_at: string | null;
  profile_version: number;
  last_pattern_update: string | null;
  created_at: string;
  updated_at: string;
}

export interface OnboardingData {
  name: string;
  roles: string[];
  roleOther: string | null;
  goals: Array<{
    text: string;
    phase: 'exploring' | 'planned' | 'executing';
    priority: number;
  }>;
}

interface ProfileStore {
  profile: UserProfile | null;
  isLoading: boolean;
  hasCompletedOnboarding: boolean;

  // Actions
  loadProfile: () => Promise<void>;
  createProfileFromOnboarding: (data: OnboardingData) => Promise<void>;
  updateGoal: (goalId: string, updates: Partial<Goal>) => Promise<void>;
  addGoal: (goal: Omit<Goal, 'id'>) => Promise<void>;
  reprioritizeGoals: (goalIds: string[]) => Promise<void>;
  completeGoal: (goalId: string) => Promise<void>;
  dropGoal: (goalId: string) => Promise<void>;
  updatePartnrPreferences: (
    prefs: Partial<UserProfile['partnr_preferences']>
  ) => Promise<void>;
  setProfile: (profile: UserProfile) => void;
  resetStore: () => void;
}

export const useProfileStore = create<ProfileStore>((set, get) => ({
  profile: null,
  isLoading: true,
  hasCompletedOnboarding: false,

  loadProfile: async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        set({ profile: null, isLoading: false, hasCompletedOnboarding: false });
        return;
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.warn('Failed to load user profile from DB:', error.message);
        // Fall back to localStorage cache or existing store profile
        const existing = get();
        if (existing.profile) {
          set({ isLoading: false });
        } else {
          const cached = loadProfileFromLocalStorage();
          if (cached && cached.onboarding_completed_at) {
            set({ profile: cached, isLoading: false, hasCompletedOnboarding: true });
          } else {
            set({ profile: null, isLoading: false, hasCompletedOnboarding: false });
          }
        }
        return;
      }

      if (data) {
        const profile: UserProfile = {
          ...data,
          roles: (data.roles as string[]) || [],
          role_other: data.role_other || null,
          goals: ((data.goals as unknown) as Goal[]) || [],
          patterns: (data.patterns as Record<string, any>) || {},
          partnr_preferences:
            (data.partnr_preferences as Record<string, any>) || {},
          mind_dumps: (data.mind_dumps as Array<Record<string, any>>) || [],
        };
        cacheProfileToLocalStorage(profile);
        set({
          profile,
          isLoading: false,
          hasCompletedOnboarding: !!data.onboarding_completed_at,
        });
      } else {
        // No profile row found — check store or localStorage
        const existing = get();
        if (existing.profile && existing.hasCompletedOnboarding) {
          set({ isLoading: false });
        } else {
          const cached = loadProfileFromLocalStorage();
          if (cached && cached.onboarding_completed_at) {
            set({ profile: cached, isLoading: false, hasCompletedOnboarding: true });
          } else {
            set({ profile: null, isLoading: false, hasCompletedOnboarding: false });
          }
        }
      }
    } catch (err) {
      console.error('[ProfileStore] loadProfile crashed:', err);
      // Ensure loading state clears even on unexpected errors
      const cached = loadProfileFromLocalStorage();
      if (cached && cached.onboarding_completed_at) {
        set({ profile: cached, isLoading: false, hasCompletedOnboarding: true });
      } else {
        set({ profile: null, isLoading: false, hasCompletedOnboarding: false });
      }
    }
  },

  createProfileFromOnboarding: async (data: OnboardingData) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const now = new Date().toISOString();
    const mindDumpId = crypto.randomUUID();

    // 1. Build goal objects
    const goals: Goal[] = data.goals.map((g) => ({
      id: crypto.randomUUID(),
      text: g.text,
      phase: g.phase,
      priority: g.priority,
      board_id: null,
      status: 'active' as const,
      created_at: now,
      completed_at: null,
      last_activity_at: now,
      phase_history: [{ phase: g.phase, entered_at: now }],
    }));

    const boardIds: string[] = [];
    const roleStr = data.roles.join(', ');

    // 2. Clean up any previous onboarding data (re-onboarding scenario)
    try {
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('goals')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingProfile?.goals) {
        const oldGoals = (existingProfile.goals as unknown) as Goal[];
        const oldBoardIds = oldGoals
          .map((g) => g.board_id)
          .filter(Boolean) as string[];

        if (oldBoardIds.length > 0) {
          // Delete old goal_board_links
          await supabase
            .from('goal_board_links')
            .delete()
            .eq('user_id', user.id);

          // Delete old onboarding boards (and their items via cascade)
          await supabase
            .from('boards')
            .delete()
            .eq('user_id', user.id)
            .in('id', oldBoardIds);
        }
      }
    } catch (cleanupErr) {
      console.warn('Non-critical: cleanup of old onboarding data failed:', cleanupErr);
    }

    // 3. Find or create the Home board (the root board with parent_id = null)
    let homeBoardId: string | null = null;
    try {
      const { data: existingHome } = await supabase
        .from('boards')
        .select('id')
        .eq('user_id', user.id)
        .is('parent_id', null)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (existingHome) {
        homeBoardId = existingHome.id;
      } else {
        const { data: newHome, error: homeError } = await supabase
          .from('boards')
          .insert({ user_id: user.id, name: 'Home', parent_id: null })
          .select('id')
          .single();
        if (!homeError && newHome) {
          homeBoardId = newHome.id;
        }
      }
    } catch (homeErr) {
      console.warn('Failed to find/create home board:', homeErr);
    }

    // 4. Create boards for each goal (as children of the Home board)
    for (let i = 0; i < goals.length; i++) {
      const goal = goals[i];
      const colorId =
        FOLDER_COLORS[i % FOLDER_COLORS.length]?.id || 'periwinkle';

      try {
        const { data: board, error: boardError } = await supabase
          .from('boards')
          .insert({
            user_id: user.id,
            name: goal.text,
            icon_color: colorId,
            parent_id: homeBoardId,
          })
          .select('id')
          .single();

        if (boardError || !board) {
          console.error('Failed to create board for goal:', goal.text, boardError);
          continue;
        }

        boardIds.push(board.id);
        goal.board_id = board.id;

        // Create a board_link item on the Home board so the folder is visible
        if (homeBoardId) {
          const boardLinkX = 100 + (i % 3) * 260;
          const boardLinkY = 100 + Math.floor(i / 3) * 220;
          const { error: boardLinkError } = await supabase.from('items').insert({
            user_id: user.id,
            board_id: homeBoardId,
            item_type: 'board_link',
            content: { linked_board_id: board.id, name: goal.text, color: colorId },
            position_x: boardLinkX,
            position_y: boardLinkY,
            width: 200,
            height: 150,
          });
          if (boardLinkError) {
            console.error('Failed to create board_link for goal:', goal.text, boardLinkError);
          }
        }

        // Create starter cards for this board (non-blocking per-goal)
        try {
          const starterCards = await generateStarterCards(
            { text: goal.text, phase: goal.phase },
            roleStr
          );

          for (const card of starterCards) {
            const { error: itemError } = await supabase.from('items').insert({
              user_id: user.id,
              board_id: board.id,
              item_type: 'text_card',
              content: { title: card.title || '', body: card.text },
              position_x: card.position.x,
              position_y: card.position.y,
              width: 240,
              height: 160,
            });
            if (itemError) {
              console.warn('Failed to create starter card:', itemError);
            }
          }
        } catch (cardErr) {
          console.warn('Starter card generation failed for goal:', goal.text, cardErr);
        }

        // Create goal-board link
        const { error: linkError } = await supabase
          .from('goal_board_links')
          .insert({
            user_id: user.id,
            goal_id: goal.id,
            board_id: board.id,
            relevance_score: 1.0,
          });
        if (linkError) {
          console.warn('Failed to create goal-board link:', linkError);
        }
      } catch (boardErr) {
        console.error('Board creation loop error for goal:', goal.text, boardErr);
      }
    }

    // 5. Create/update the user profile
    const mindDump = {
      id: mindDumpId,
      type: 'onboarding',
      timestamp: now,
      raw_input: {
        name: data.name,
        roles: data.roles,
        role_other: data.roleOther,
        goals: data.goals,
      },
      goals_extracted: goals.map((g) => g.id),
      boards_created: boardIds,
    };

    const profileData = {
      user_id: user.id,
      display_name: data.name,
      role: data.roles.join(', '),
      roles: data.roles,
      role_other: data.roleOther,
      goals: goals as any,
      patterns: {
        capture_frequency: {
          daily_avg: 0,
          peak_hours: [],
          preferred_types: [],
          last_30_days: 0,
        },
        thinking_style: {
          spatial_usage: null,
          board_depth: null,
          revisit_rate: 0,
          avg_session_minutes: 0,
        },
        content_themes: [],
        goal_velocity: {
          avg_days_to_phase_change: null,
          completion_rate: 0,
          abandonment_rate: 0,
        },
      },
      partnr_preferences: {
        verbosity: 'moderate',
        nudge_receptivity: 'medium',
        recommendation_accuracy: {
          accepted: 0,
          dismissed: 0,
          acceptance_rate: 0,
        },
        topics_engaged: [],
        topics_ignored: [],
      },
      mind_dumps: [mindDump] as any,
      onboarding_completed_at: now,
      profile_version: 1,
      updated_at: now,
    };

    // 7. Build the full profile object for the store (regardless of DB success)
    const fullProfile: UserProfile = {
      id: '',
      ...profileData,
      roles: data.roles,
      role_other: data.roleOther,
      goals,
      mind_dumps: [mindDump],
      last_pattern_update: null,
      created_at: now,
    };

    // 8. Try to persist profile to DB (non-fatal if table doesn't exist)
    try {
      const { error: upsertErr } = await supabase
        .from('user_profiles')
        .upsert(profileData, { onConflict: 'user_id' });

      if (upsertErr) {
        console.warn('Profile upsert failed, trying fallback:', upsertErr.message);
        const { roles: _r, role_other: _ro, ...fallbackData } = profileData;
        const { error: fallbackErr } = await supabase
          .from('user_profiles')
          .upsert(fallbackData, { onConflict: 'user_id' });
        if (fallbackErr) {
          console.warn('Profile DB persist failed (will use localStorage):', fallbackErr.message);
        }
      }
    } catch (dbErr) {
      console.warn('Profile DB persist threw (will use localStorage):', dbErr);
    }

    // 9. Log the mind dump event (non-critical)
    try {
      await supabase.from('profile_events').insert({
        user_id: user.id,
        event_type: 'mind_dump_completed',
        payload: {
          type: 'onboarding',
          goals_count: goals.length,
          boards_created: boardIds.length,
        },
      });
    } catch (eventErr) {
      console.warn('Failed to log onboarding event:', eventErr);
    }

    // 10. Update store and cache to localStorage
    cacheProfileToLocalStorage(fullProfile);

    // Set the profile in the store but do NOT set hasCompletedOnboarding yet.
    // The Canvas onComplete callback sets it AFTER fetchBoards() so the
    // MindDumpFlow stays mounted (user sees board preview + hook step) and
    // boards are loaded before the main canvas renders.
    set({
      profile: fullProfile,
    });
  },

  updateGoal: async (goalId, updates) => {
    const { profile } = get();
    if (!profile) return;

    const updatedGoals = profile.goals.map((g) =>
      g.id === goalId ? { ...g, ...updates } : g
    );

    const { error } = await supabase
      .from('user_profiles')
      .update({
        goals: updatedGoals as any,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', profile.user_id);

    if (!error) {
      set({ profile: { ...profile, goals: updatedGoals } });
    }
  },

  addGoal: async (goalData) => {
    const { profile } = get();
    if (!profile) return;

    const newGoal: Goal = {
      ...goalData,
      id: crypto.randomUUID(),
    };
    const updatedGoals = [...profile.goals, newGoal];

    const { error } = await supabase
      .from('user_profiles')
      .update({
        goals: updatedGoals as any,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', profile.user_id);

    if (!error) {
      set({ profile: { ...profile, goals: updatedGoals } });
    }
  },

  reprioritizeGoals: async (goalIds) => {
    const { profile } = get();
    if (!profile) return;

    const updatedGoals = profile.goals.map((g) => {
      const newPriority = goalIds.indexOf(g.id);
      return newPriority >= 0 ? { ...g, priority: newPriority + 1 } : g;
    });

    const { error } = await supabase
      .from('user_profiles')
      .update({
        goals: updatedGoals as any,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', profile.user_id);

    if (!error) {
      set({ profile: { ...profile, goals: updatedGoals } });
    }
  },

  completeGoal: async (goalId) => {
    const store = get();
    await store.updateGoal(goalId, {
      status: 'completed',
      completed_at: new Date().toISOString(),
    });
  },

  dropGoal: async (goalId) => {
    const store = get();
    await store.updateGoal(goalId, {
      status: 'dropped',
    });
  },

  updatePartnrPreferences: async (prefs) => {
    const { profile } = get();
    if (!profile) return;

    const updatedPrefs = { ...profile.partnr_preferences, ...prefs };

    const { error } = await supabase
      .from('user_profiles')
      .update({
        partnr_preferences: updatedPrefs,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', profile.user_id);

    if (!error) {
      set({
        profile: { ...profile, partnr_preferences: updatedPrefs },
      });
    }
  },

  setProfile: (profile) => {
    set({
      profile,
      isLoading: false,
      hasCompletedOnboarding: !!profile.onboarding_completed_at,
    });
  },

  resetStore: () => {
    set({
      profile: null,
      isLoading: true,
      hasCompletedOnboarding: false,
    });
  },
}));
